import { debugLog } from "@/utils/debugLog";

class PCMPlayer {
  constructor(option, setAssistantStatus) {
    this.init(option);
    this.setAssistantStatus = setAssistantStatus;
  }

  init(option) {
    const defaultOption = {
      inputCodec: "Int16",
      channels: 1,
      sampleRate: 44100,
      flushTime: 500,
      fftSize: 2048,
    };

    this.option = Object.assign({}, defaultOption, option);
    this.samples = new Float32Array();
    // this.interval = setInterval(this.flush.bind(this), this.option.flushTime);
    this.convertValue = this.getConvertValue();
    this.typedArray = this.getTypedArray();
    this.initAudioContext();
    this.bindAudioContextEvent();
    this.wsConnected = false;
    this.isCurrentlyPlaying = false;
    this.activeBufferSources = new Set();
    this.playedMs = 0;
    // Total silence skipped by flush()'s startTime clamp during buffer
    // underruns. Subtracted from the anchored clock so playedMs tracks
    // audio actually emitted, which is the domain of TTS word timestamps.
    this.silentGapMs = 0;
  }

  reset() {
    this.audioPlayed = false;
    this.doneChunkRecieved = false;
    this.setAssistantStatus("speaking");
  }

  done() {
    this.doneChunkRecieved = true;
  }

  getAudioPlayed() {
    return this.audioPlayed;
  }

  getConvertValue() {
    const inputCodecs = {
      Int8: 128,
      Int16: 32768,
      Int32: 2147483648,
      Float32: 1,
    };
    if (!inputCodecs[this.option.inputCodec])
      throw new Error(
        "Wrong codec. Please use Int8, Int16, Int32, or Float32.",
      );
    return inputCodecs[this.option.inputCodec];
  }

  getTypedArray() {
    const typedArrays = {
      Int8: Int8Array,
      Int16: Int16Array,
      Int32: Int32Array,
      Float32: Float32Array,
    };
    if (!typedArrays[this.option.inputCodec])
      throw new Error(
        "Wrong codec. Please use Int8, Int16, Int32, or Float32.",
      );
    return typedArrays[this.option.inputCodec];
  }

  initAudioContext() {
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    this.gainNode = this.audioCtx.createGain();
    this.gainNode.gain.value = 1.0;
    this.gainNode.connect(this.audioCtx.destination);
    this.startTime = this.audioCtx.currentTime;

    this.analyserNode = this.audioCtx.createAnalyser();
    this.analyserNode.fftSize = this.option.fftSize;
    this.analyserNode.smoothingTimeConstant = 0.8;

    this.gainNode.connect(this.analyserNode);
    this.audioT0 = null;
  }

  static isTypedArray(data) {
    return (
      (data.byteLength &&
        data.buffer &&
        data.buffer.constructor == ArrayBuffer) ||
      data.constructor == ArrayBuffer
    );
  }

  isSupported(data) {
    if (!PCMPlayer.isTypedArray(data))
      throw new Error("Input must be an ArrayBuffer or TypedArray.");
    return true;
  }

  feed(data) {
    this.isSupported(data);
    data = this.getFormattedValue(data);
    const tmp = new Float32Array(this.samples.length + data.length);
    tmp.set(this.samples, 0);
    tmp.set(data, this.samples.length);
    this.samples = tmp;
    this.flush();
  }

  getFormattedValue(data) {
    if (data.constructor == ArrayBuffer) {
      data = new this.typedArray(data);
    } else {
      data = new this.typedArray(data.buffer);
    }

    let float32 = new Float32Array(data.length);
    for (let i = 0; i < data.length; i++) {
      float32[i] = data[i] / this.convertValue;
    }
    return float32;
  }

  volume(volume) {
    this.gainNode.gain.value = volume;
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.samples = null;
    this.audioCtx.close();
    this.audioCtx = null;
  }

  flush() {
    debugLog("[FLUSHING]");
    if (!this.samples.length) return;
    const self = this;
    const bufferSource = this.audioCtx.createBufferSource();

    // Track buffer source
    this.activeBufferSources.add(bufferSource);

    // Handle start of playback
    const startTime = this.startTime;
    setTimeout(
      () => {
        if (!this.isCurrentlyPlaying) {
          this.isCurrentlyPlaying = true;
          if (typeof this.option.onplay === "function") {
            this.option.onplay();
          }
        }
      },
      (startTime - this.audioCtx.currentTime) * 1000,
    );

    // Handle end of playback
    bufferSource.onended = function (event) {
      self.activeBufferSources.delete(bufferSource);

      // Only trigger onended if no other buffers are playing
      if (self.activeBufferSources.size === 0) {
        self.isCurrentlyPlaying = false;
        if (typeof self.option.onended === "function") {
          self.option.onended(this, event);
        }
      }
    };

    const length = this.samples.length / this.option.channels;
    const audioBuffer = this.audioCtx.createBuffer(
      this.option.channels,
      length,
      this.option.sampleRate,
    );

    for (let channel = 0; channel < this.option.channels; channel++) {
      const audioData = audioBuffer.getChannelData(channel);
      let offset = channel;
      let decrement = 50;

      for (let i = 0; i < length; i++) {
        audioData[i] = this.samples[offset];
        if (i < 50) {
          audioData[i] = (audioData[i] * i) / 50;
        }
        if (i >= length - 51) {
          audioData[i] = (audioData[i] * decrement--) / 50;
        }
        offset += this.option.channels;
      }
    }

    if (this.startTime < this.audioCtx.currentTime) {
      // The stream ran dry at startTime and resumes now: the clamp amount is
      // the exact silence, measured on the audio clock rather than sampled by
      // the main thread. Only meaningful once this generation is anchored.
      if (this.audioT0 !== null) {
        this.silentGapMs += (this.audioCtx.currentTime - this.startTime) * 1000;
      }
      this.startTime = this.audioCtx.currentTime;
    }

    // Anchor the clock to the scheduled start of this generation's first
    // buffer. Not audioCtx.currentTime: at a generation boundary the previous
    // generation's audio is usually still queued ahead, so currentTime would
    // anchor early and count that tail as this generation's playback.
    if (this.audioT0 === null) {
      this.audioT0 = this.startTime;
      this.playedMs = 0;
      this.silentGapMs = 0;
    }

    bufferSource.buffer = audioBuffer;
    bufferSource.connect(this.gainNode);
    bufferSource.connect(this.analyserNode);
    bufferSource.start(this.startTime);
    this.startTime += audioBuffer.duration;
    if (this.doneChunkRecieved) {
      const interval = setInterval(() => {
        if (this.audioCtx?.currentTime > this.startTime) {
          debugLog("[DONE PLAYING]");
          this.audioPlayed = true;
          this.setAssistantStatus("idle");

          window.animationController?.call("SHRINK");

          clearInterval(interval);
        }
      }, 100);
    }
    this.samples = new Float32Array();
  }

  isPlaying() {
    return this.isCurrentlyPlaying && this.activeBufferSources.size > 0;
  }

  /**
   * Milliseconds of this generation's TTS audio that have actually reached the
   * speakers. Read straight off the audio graph, so the value does not depend
   * on when — or how often — this is called: rAF throttling in a hidden tab,
   * event-loop lag, and delayed onended dispatch all cost nothing.
   */
  tickPlayedMs() {
    if (this.audioT0 === null) return this.playedMs;
    // Clamped at startTime (the end of scheduled audio) so an underrun parks
    // the clock on the last sample instead of running past it, and clamped at
    // 0 so a generation whose audio is still queued ahead stays at t=0.
    const now = Math.min(this.audioCtx.currentTime, this.startTime);
    this.playedMs = Math.max(0, (now - this.audioT0) * 1000 - this.silentGapMs);
    return this.playedMs;
  }

  async pause() {
    if (this.isPlaying()) {
      const bufferDuration = this.samples.length / this.option.sampleRate;
      await new Promise((resolve) =>
        setTimeout(resolve, bufferDuration * 1000),
      );
    }
    if (this.audioCtx.state === "running") {
      await this.audioCtx.suspend();
    }
  }

  async continue() {
    if (this.audioCtx.state === "suspended") {
      await this.audioCtx.resume();
    }
  }

  // clear all buffers and set the assistant status to idle
  async clear() {
    this.activeBufferSources.forEach((source) => {
      try {
        source.stop();
      } catch (e) {
        debugLog("Buffer already stopped");
      }
    });
    this.activeBufferSources.clear();
    this.isCurrentlyPlaying = false;
    this.samples = new Float32Array();
    this.startTime = this.audioCtx.currentTime;
    this.audioT0 = null;
    this.playedMs = 0;
    this.silentGapMs = 0;
  }

  bindAudioContextEvent() {
    const self = this;
    if (typeof self.option.onstatechange === "function") {
      this.audioCtx.onstatechange = function (event) {
        self.audioCtx &&
          self.option.onstatechange(this, event, self.audioCtx.state);
      };
    }
  }

  /**
   * Returns the analyser node
   */
  getAnalyserNode() {
    return this.analyserNode;
  }

  /**
   * Gets frequency spectrum data (FFT output).
   * @returns Uint8Array of frequency data.
   */
  getFrequencyData() {
    if (!this.analyserNode) return null;
    const frequencyData = new Uint8Array(this.analyserNode.frequencyBinCount);
    this.analyserNode.getByteFrequencyData(frequencyData);
    return frequencyData;
  }

  /**
   * Gets time-domain (waveform) data.
   * @returns Uint8Array of waveform data.
   */
  getTimeDomainData() {
    if (!this.analyserNode) return null;
    const timeDomainData = new Uint8Array(this.analyserNode.fftSize);
    this.analyserNode.getByteTimeDomainData(timeDomainData);
    return timeDomainData;
  }

  /**
   * Get the RMS of the audio data
   * @returns {number} RMS of the audio data
   */
  getRMS() {
    const timeDomainData = this.getTimeDomainData();
    let sum = 0;
    for (let i = 0; i < timeDomainData.length; i++) {
      const value = (timeDomainData[i] - 128) / 128;
      sum += value * value;
    }
    return Math.sqrt(sum / timeDomainData.length);
  }

  getSmoothedAmplitude(sigma = 5, kernelSize = 15) {
    if (this.gainNode?.gain.value === 0) return 0;
    const timeDomainData = this.getTimeDomainData();
    if (!timeDomainData) return null;

    let normalizedData = new Float32Array(timeDomainData.length);
    for (let i = 0; i < timeDomainData.length; i++) {
      normalizedData[i] = (timeDomainData[i] - 128) / 128;
    }

    let kernel = new Float32Array(kernelSize);
    let sum = 0;
    const center = Math.floor(kernelSize / 2);
    for (let i = 0; i < kernelSize; i++) {
      let x = i - center;
      kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
      sum += kernel[i];
    }
    for (let i = 0; i < kernelSize; i++) {
      kernel[i] /= sum;
    }

    let smoothedData = new Float32Array(normalizedData.length);
    for (let i = 0; i < normalizedData.length; i++) {
      let weightedSum = 0;
      let weightTotal = 0;
      for (let j = 0; j < kernelSize; j++) {
        let index = i + j - center;
        if (index >= 0 && index < normalizedData.length) {
          weightedSum += normalizedData[index] * kernel[j];
          weightTotal += kernel[j];
        }
      }
      smoothedData[i] = weightedSum / weightTotal;
    }

    let sumSquares = 0;
    for (let i = 0; i < smoothedData.length; i++) {
      sumSquares += smoothedData[i] * smoothedData[i];
    }
    // console.log("sumSquares",Math.sqrt(sumSquares / smoothedData.length));
    return Math.sqrt(sumSquares / smoothedData.length);
  }

  setWsConnected() {
    this.wsConnected = true;
  }
}

export default PCMPlayer;
